import { Component, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';

import { Router } from '@angular/router';

import { UserService } from '@core/services/user.service';
import { ThemeService } from '@core/services/theme.service';
import { AppPlanService } from '@core/services/app-plan.service';
import { CompanyService } from '@core/services/company.service';
import { ProfileValidationService } from '@core/services/profile-validation.service';
import { LoggingService } from '@core/services/logging.service';

import { User } from '@core/models/user';
import { Company } from '@core/models/company';
import { AppPlan } from '@core/models/app-plan';

@Component({
  selector: 'app-select-plan',
  templateUrl: './select-plan.component.html',
  styleUrl: './select-plan.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectPlanComponent implements OnDestroy {
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private userService = inject(UserService);
  private planService = inject(AppPlanService);
  private companyService = inject(CompanyService);
  private profileValidationService = inject(ProfileValidationService);
  private loggingService = inject(LoggingService);

  private user?: User;
  private company?: Company;
  public plans: AppPlan[] = [];

  // Registration flow state
  public isFromRegistrationFlow = false;
  public missingRequirements: string[] = [];
  public showValidationWarning = false;

  constructor() {
    // Check if user is coming from registration flow
    this.isFromRegistrationFlow = this.router.url.includes(
      '/register/select-plan',
    );

    this.themeService.setSelectPlanPageLayout(true);

    this.userService
      .getUserModel()
      .subscribe(async (userModel: User | null) => {
        if (userModel) {
          this.user = userModel;

          // Load company data for new multi-tenant architecture
          if (userModel.currentCompanyId) {
            const currentCompanyId = userModel.currentCompanyId;
            try {
              const company = await this.companyService.getCompany(currentCompanyId);
              if (company) {
                this.company = company;

                // Validate registration flow requirements
                if (this.isFromRegistrationFlow) {
                  this.validateRegistrationRequirements();
                }
              }
            } catch (error) {
              console.error('Error loading company:', error);
            }
          }
        }
      });

    // Load plans from JSON for testing - will switch to Firestore later
    this.planService.getOrderedPlans().subscribe({
      next: (plans) => {
        this.plans = plans;
      },
      error: (error) => {
        console.error('Error loading plans:', error);
        // Fallback to Firestore if JSON loading fails
        this.planService
          .getCollection$(null, null, 'order')
          .then((appPlanSnapShot: any) => {
            this.plans = [];
            appPlanSnapShot.forEach((doc: any) => {
              this.plans = [...this.plans, new AppPlan(doc.data())];
            });
          });
      },
    });
  }

  ngOnDestroy(): void {
    this.themeService.setSelectPlanPageLayout(false);
  }

  /**
   * Validate registration requirements before plan selection
   */
  private validateRegistrationRequirements(): void {
    if (!this.user || !this.company) return;

    // Don't show validation warning initially - only validate when user clicks a plan
    // This prevents confusing "missing first/last name" errors if user data is still loading
    // or if fields were set during OAuth sign-up but haven't synced yet
    this.showValidationWarning = false;
    this.missingRequirements = [];
  }

  /**
   * Map plan to tier name
   */
  private mapPlanToTier(plan: AppPlan): 'free' | 'trial' | 'premium' {
    if (plan.appTier === 'free' || plan.appTier === 'trial' || plan.appTier === 'premium') {
      return plan.appTier;
    }

    if (plan.name.toLowerCase().includes('premium')) {
      return 'premium';
    } else if (
      plan.name.toLowerCase().includes('essential') ||
      plan.name.toLowerCase().includes('trial')
    ) {
      return 'trial';
    }

    return 'trial'; // Default to trial
  }

  /**
   * Check if user can select a specific tier
   */
  canSelectTier(_tierName: 'free' | 'trial' | 'premium'): boolean {
    if (!this.user || !this.company) return false;

    return this.profileValidationService.canSelectTier(
      this.user,
      this.company,
      _tierName,
    );
  }

  /**
   * Get user-friendly plan description for tier
   */
  getTierDescription(plan: AppPlan): string {
    const tierName = this.mapPlanToTier(plan);

    switch (tierName) {
      case 'free':
        return 'Basic features with demo data';
      case 'trial':
        return 'Trial arranged by our sales team';
      case 'premium':
        return 'Full platform access with unlimited features';
      default:
        return '';
    }
  }
}
